<template>
  <div class="app-page" :class="rootClasses">
    <!-- hideTitle: Überschrift nur für Screenreader; der Kopf erscheint nur noch für Aktionen (Handy-Knopfzeile) -->
    <h1 v-if="hideTitle" class="app-sr-only">{{ pageTitle }}</h1>
    <header v-if="!hideTitle || hasSlot('actions')" class="app-page__head" :class="headClasses()">
      <div v-if="!hideTitle" class="app-page__heading">
        <span v-if="pageIcon && !lean" class="app-page__tile" aria-hidden="true">
          <app-icon :name="pageIcon" :size="18" />
        </span>
        <div class="app-page__titles">
          <h1 class="app-page__title">{{ pageTitle }}</h1>
          <p v-if="subtitle" class="app-page__subtitle">{{ subtitle }}</p>
        </div>
      </div>
      <div v-if="hasSlot('actions')" class="app-page__actions"><slot name="actions" /></div>
    </header>
    <div v-if="hasToolbar()" class="app-page__toolbar">
      <slot name="toolbar" />
      <div v-if="hasSlot('toolbar-end')" class="app-page__toolbar-end"><slot name="toolbar-end" /></div>
    </div>
    <div class="app-page__body" :class="{ 'app-page__body--row': hasSlot('detail') }">
      <slot />
      <slot name="detail" />
    </div>
  </div>
</template>

<script>
// Seitenrahmen (Spezifikation 2.10): Seitenkopf (Icon-Kachel 32, Titel 18/24 600, Aktionen rechts), Werkzeugleiste,
// Inhalt. Titel und Icon kommen ohne Props aus der Navigation (findEntry aus src/shell/navigation.js, Abschnitt 3).
// Slots: actions (rechts im Kopf), toolbar (links in der Werkzeugleiste), toolbar-end (rechts), default (Inhalt),
// detail (rechts neben dem Inhalt; mit diesem Slot ist der Inhaltsbereich eine Flex-Zeile, sonst eine Flex-Spalte).
// fill: Seite füllt das Blatt exakt (innen scrollende Bereiche); auf dem Handy natürliche Höhe.
// lean: leichter Seitenkopf unter der Systemleiste – ohne Icon-Kachel und ohne Trennlinie, Titel 20/28 (Dashboard und
// Verkäufe, Kundenwunsch 12.10.2026); subtitle: Unterzeile unter dem Titel (13/18 --ink-3).
// hideTitle: keine sichtbare Überschrift (die Brotkrumen der Systemleiste nennen die Seite) – die Werkzeugleiste steht
// direkt unter der Systemleiste, die h1 bleibt für Screenreader. Derzeit ungenutzt (Verkäufe zeigt den Titel wieder,
// Kundenwunsch 12.10.2026).
// phoneSurface (Standard true): auf dem Handy weiße Fläche, sonst Fensterfläche (#eef0f3).
// Setzt --page-x (Seitenrand 20, Tablet hoch 16, Handy 12) für eigene Inhaltsbereiche (Klasse app-page-pad).
// Der Default-Slot wird nie per v-if geschaltet (Refs der Seiten hängen daran).
import AppIcon from './AppIcon.vue';

// navigation.js legt Gruppe 1b an. require.context liefert einen leeren Kontext, solange die Datei fehlt
// (kein Kompilierfehler), und nimmt sie automatisch auf, sobald sie existiert.
const navContext = require.context('../../shell', false, /^\.\/navigation\.js$/);

function lookupEntry(path) {
  const keys = navContext.keys();
  if (!keys.length) {
    return null;
  }
  const nav = navContext(keys[0]);
  const findEntry = nav && (nav.findEntry || (nav.default && nav.default.findEntry));
  if (typeof findEntry !== 'function') {
    return null;
  }
  try {
    const found = findEntry(path);
    return found ? (found.entry || found.group || null) : null;
  } catch (e) {
    return null;
  }
}

export default {
  name: 'AppPage',
  components: { AppIcon },
  props: {
    icon: { type: String, default: null },
    title: { type: String, default: null },
    fill: { type: Boolean, default: false },
    lean: { type: Boolean, default: false },
    hideTitle: { type: Boolean, default: false },
    subtitle: { type: String, default: null },
    phoneSurface: { type: Boolean, default: true }
  },
  computed: {
    handy() {
      return !!(this.$dichte && this.$dichte.handy);
    },
    narrow() {
      const touch = !!(this.$dichte && this.$dichte.touch);
      const width = this.$vuetify && this.$vuetify.breakpoint ? this.$vuetify.breakpoint.width : 1280;
      return !this.handy && touch && width < 960;
    },
    navEntry() {
      return this.$route ? lookupEntry(this.$route.path) : null;
    },
    pageTitle() {
      return this.title || (this.navEntry && this.navEntry.title) || '';
    },
    pageIcon() {
      return this.icon || (this.navEntry && this.navEntry.icon) || null;
    },
    rootClasses() {
      return {
        'app-page--fill': this.fill && !this.handy,
        'app-page--natural': !this.fill || this.handy,
        'app-page--phone': this.handy,
        'app-page--narrow': this.narrow,
        'app-page--lean': this.lean,
        'app-page--notitle': this.hideTitle && !this.handy,
        'app-page--plain': this.handy && !this.phoneSurface
      };
    }
  },
  methods: {
    // Slot-Abfragen als Methoden: $slots ist nicht reaktiv, Methoden laufen bei jedem Rendern neu
    headClasses() {
      return {
        'app-page__head--line': !this.hasToolbar() && !this.lean,
        'app-page__head--toolbar': this.hasToolbar(),
        'app-page__head--empty': this.handy && !this.hasSlot('actions')
      };
    },
    hasSlot(name) {
      return !!(this.$slots[name] || this.$scopedSlots[name]);
    },
    hasToolbar() {
      return this.hasSlot('toolbar') || this.hasSlot('toolbar-end');
    }
  }
};
</script>
